import { useQuery } from "@tanstack/react-query"
import { useAuth } from "@/hooks/useAuth"
import { studioApi } from "@/lib/studio-api"
import { isAdmin } from "@/lib/roles"

/**
 * Who may use Creator Studio, and whether the user is on a Studio-only client
 * (the nav then shows Studio and the account basics, nothing else). Answers
 * every signed-in user, so it is safe to ask from the shell.
 */
export function useStudioAccess() {
  const { isAuthenticated, user } = useAuth()
  return useQuery({
    queryKey: ["studio", "access", user?.id],
    queryFn: () => studioApi.access(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

/**
 * How much of Forge's nav to show. Until /studio/access answers, a non-admin
 * might be Studio-only, so hide the license nav rather than flash it at them.
 */
export function useStudioNav() {
  const { user } = useAuth()
  const { data: access, isLoading } = useStudioAccess()
  const studioOnly = !!access?.studio_only
  return {
    canStudio: !!access?.can_access,
    studioOnly,
    hideLicenseNav: studioOnly || (isLoading && !isAdmin(user)),
  }
}

export function useStudioProfile(slug: string | undefined) {
  return useQuery({
    queryKey: ["studio", "profile", slug],
    queryFn: () => studioApi.profiles.get(slug!),
    enabled: !!slug,
    staleTime: 30 * 1000,
  })
}
